import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsChild } from '../../hooks/useModules.js';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { MEASUREMENT_KEYS, MEASUREMENT_LIMITS, measurementInRange } from '@kraftwerk/shared';
import type { MeasurementKey } from '@kraftwerk/shared';
import { journalFor, saveBodyWeight, saveJournal, saveMeasurement } from '../../db/journal.js';
import type { JournalFields } from '../../db/journal.js';
import { ScalePicker } from '../ScalePicker.js';
import { JournalPhotos } from './JournalPhotos.js';
import { useToday } from '../../hooks/useToday.js';
import type { LocalJournalEntry } from '../../db/local.js';

/** Schritt beim Schlaf: halbe Stunden genügen, genauer weiß es morgens keiner. */
const SLEEP_STEP = 0.5;

/**
 * Schlaf in halben Stunden — auf „Heute" und im Tagebuch derselbe Regler.
 *
 * Ohne Eintrag startet `+` bei 7 statt bei 0: vierzehn Tipps bis zu einer
 * üblichen Nacht wären eine Hürde, und der Wert steht sichtbar da, bevor er zählt.
 */
export function SleepStepper({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <span className="kw-label">{t('journal.sleepHours')}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="kw-step"
          aria-label={t('journal.sleepLess')}
          disabled={value === null || value <= 0}
          onClick={() => onChange(Math.max(0, (value ?? 0) - SLEEP_STEP))}
        >
          −
        </button>
        {/* Ohne Wert kein Riesentext: „nicht eingetragen“ brach in der
            Zahlenschrift bei 375 px auf zwei Zeilen um. */}
        <output
          className={
            value === null
              ? 'min-w-24 flex-1 text-center text-ink-muted'
              : 'kw-figure min-w-24 flex-1 text-center text-3xl'
          }
          aria-live="polite"
        >
          {value === null ? t('journal.notEntered') : t('journal.hours', { hours: value })}
        </output>
        <button
          type="button"
          className="kw-step"
          aria-label={t('journal.sleepMore')}
          disabled={value !== null && value >= 16}
          onClick={() => onChange(value === null ? 7 : value + SLEEP_STEP)}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Ein Tag im Tagebuch, vollständig — heute wie vor drei Wochen.
 *
 * Seit 18.10.2026 erst **lesen**, dann ändern: Steht schon etwas da, zeigt
 * die Kachel eine Zusammenfassung und „Bearbeiten“ — vorher lagen die Skalen
 * offen, und beim Blättern durch alte Tage änderte ein Fehltipp die Stimmung
 * von letzter Woche. Ein leerer heutiger Tag öffnet gleich das Formular, ein
 * leerer vergangener sagt „Nichts eingetragen“ und bietet „Nachtragen“.
 *
 * Im Formular wird jeder Wert beim Antippen gespeichert, ohne Speichern-Knopf.
 * Nicht Eingetragenes bleibt leer und wird nie als 0 gelesen.
 */
export function JournalDay({
  date,
  startEditing = false,
}: {
  date: string;
  /** Aus der Heute-Karte „Befinden“: gleich das Formular. */
  startEditing?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const child = useIsChild();
  const noteId = useId();
  const entry = useLiveQuery(() => journalFor(date, db), [date]);
  const today = useToday();
  const [note, setNote] = useState('');
  // `null`: noch nicht entschieden — dann gilt, was zum Stand passt.
  const [editing, setEditing] = useState<boolean | null>(startEditing ? true : null);
  useEffect(() => setEditing(startEditing ? true : null), [date, startEditing]);

  useEffect(() => {
    setNote(entry?.note ?? '');
  }, [entry?.id, entry?.note]);

  const save = (changes: Partial<JournalFields>) => void saveJournal(date, changes, db);
  const filled = hasValues(entry, child);
  const mode = editing ?? (!filled && date === today);
  // Hat ein leerer heutiger Tag das Formular von selbst geöffnet, bleibt es
  // offen: Sonst sprang es nach dem ersten Wert in die Zusammenfassung, und
  // der nächste Tipp traf etwas anderes (23.10.2026 im Browser gefunden).
  // Erst entscheiden, wenn der Eintrag geladen ist (`undefined` = lädt noch).
  useEffect(() => {
    if (editing === null && entry !== undefined && !filled && date === today) setEditing(true);
  }, [editing, entry, filled, date, today]);
  const commitNote = () => {
    const next = note.trim() || null;
    if (next !== (entry?.note ?? null)) save({ note: next });
  };

  if (!mode) {
    return (
      <div className="flex flex-col gap-4">
        <StepsLine steps={entry?.stepCount} />
        {filled ? (
          <CheckInSummary entry={entry!} child={child} onEdit={() => setEditing(true)} />
        ) : (
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-ink-muted">{t('journal.nothingEntered')}</p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
            >
              {t('journal.addLater')}
            </button>
          </div>
        )}
        <JournalPhotos date={date} journalEntryId={entry?.id ?? null} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Kinder (E16): kein Gewicht. */}
      <StepsLine steps={entry?.stepCount} />
      {child ? null : <WeightField date={date} value={entry?.bodyWeightKg ?? null} />}
      {child ? null : <MeasurementFields date={date} entry={entry ?? null} />}
      <SleepStepper value={entry?.sleepHours ?? null} onChange={(v) => save({ sleepHours: v })} />
      <ScalePicker
        label={t('journal.sleepQuality')}
        low={t('journal.scale.bad')}
        high={t('journal.scale.good')}
        value={entry?.sleepQuality ?? null}
        onChange={(value) => save({ sleepQuality: value })}
      />
      <ScalePicker
        label={t('journal.energy')}
        low={t('journal.scale.low')}
        high={t('journal.scale.high')}
        value={entry?.energy ?? null}
        onChange={(value) => save({ energy: value })}
      />
      <ScalePicker
        label={t('journal.mood')}
        low={t('journal.scale.bad')}
        high={t('journal.scale.good')}
        value={entry?.mood ?? null}
        onChange={(value) => save({ mood: value })}
      />
      <ScalePicker
        label={t('journal.soreness')}
        low={t('journal.scale.strong')}
        high={t('journal.scale.none')}
        value={entry?.soreness ?? null}
        onChange={(value) => save({ soreness: value })}
      />

      <label htmlFor={noteId} className="flex flex-col gap-1">
        <span className="kw-label">{t('journal.note')}</span>
        <textarea
          id={noteId}
          value={note}
          rows={4}
          maxLength={5000}
          onChange={(event) => setNote(event.target.value)}
          onBlur={commitNote}
          className="kw-field"
        />
      </label>

      {filled ? (
        <button
          type="button"
          onClick={() => {
            commitNote();
            setEditing(false);
          }}
          className="kw-btn kw-btn-small"
        >
          {t('journal.doneEditing')}
        </button>
      ) : null}

      <JournalPhotos date={date} journalEntryId={entry?.id ?? null} />
    </div>
  );
}

/**
 * Schritte aus Apple Health (24.10.2026): nur lesen — sie kommen vom
 * Kurzbefehl. Sie machen einen Tag nicht „ausgefüllt“, sonst sprang ein
 * heutiger Tag nach der Abend-Automation vom Formular in die Zusammenfassung.
 */
function StepsLine({ steps }: { steps: number | null | undefined }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  if (typeof steps !== 'number') return null;
  return (
    <p className="text-sm">
      {t('journal.steps.value', { steps: steps.toLocaleString(i18n.language) })}
      <span className="text-ink-muted"> · {t('journal.steps.source')}</span>
    </p>
  );
}

/** Steht für diesen Tag schon irgendetwas da? Kinder haben kein Gewicht. */
function hasValues(entry: LocalJournalEntry | null | undefined, child: boolean): boolean {
  if (!entry) return false;
  return (
    entry.sleepHours !== null ||
    entry.sleepQuality !== null ||
    entry.energy !== null ||
    entry.mood !== null ||
    entry.soreness !== null ||
    (entry.note ?? '') !== '' ||
    (!child && typeof entry.bodyWeightKg === 'number') ||
    (!child && !!entry.measurements)
  );
}

/**
 * Die Leseansicht: was eingetragen ist, in Worten — und ausdrücklich, was
 * fehlt, statt es wegzulassen (ein leerer Platz sähe aus wie eine Antwort).
 */
function CheckInSummary({
  entry,
  child,
  onEdit,
}: {
  entry: LocalJournalEntry;
  child: boolean;
  onEdit: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const scale = (value: number, low: string, high: string) => {
    const word =
      value <= 1
        ? t(`journal.scale.${low}`)
        : value === 2
          ? t('journal.scaleRather', { word: t(`journal.scale.${low}`) })
          : value === 3
            ? t('journal.scaleMiddle')
            : value === 4
              ? t('journal.scaleRather', { word: t(`journal.scale.${high}`) })
              : t(`journal.scale.${high}`);
    return t('journal.scaleValue', { word, value });
  };
  const rows: { key: string; label: string; value: string | null }[] = [
    ...(child
      ? []
      : [
          {
            key: 'weight',
            label: t('journal.weight'),
            value:
              typeof entry.bodyWeightKg === 'number'
                ? t('journal.summary.weight', {
                    kg: entry.bodyWeightKg.toLocaleString(i18n.language),
                  })
                : null,
          },
        ]),
    {
      key: 'sleep',
      label: t('journal.sleepHours'),
      value:
        entry.sleepHours !== null
          ? t('journal.hours', { hours: entry.sleepHours.toLocaleString(i18n.language) })
          : null,
    },
    {
      key: 'sleepQuality',
      label: t('journal.sleepQuality'),
      value: entry.sleepQuality !== null ? scale(entry.sleepQuality, 'bad', 'good') : null,
    },
    {
      key: 'energy',
      label: t('journal.energy'),
      value: entry.energy !== null ? scale(entry.energy, 'low', 'high') : null,
    },
    {
      key: 'mood',
      label: t('journal.mood'),
      value: entry.mood !== null ? scale(entry.mood, 'bad', 'good') : null,
    },
    {
      key: 'soreness',
      label: t('journal.soreness'),
      value: entry.soreness !== null ? scale(entry.soreness, 'strong', 'none') : null,
    },
  ];
  const missing = rows.filter((row) => row.value === null).map((row) => row.label);
  // Körpermaße nimmt man alle paar Wochen, nicht täglich: Sie stehen da, wenn
  // gemessen wurde, und fehlen nicht in „Nicht eingetragen“ — sonst stünden
  // dort an fast jedem Tag sechs Zeilen.
  const measured = child
    ? []
    : MEASUREMENT_KEYS.flatMap((key) => {
        const value = entry.measurements?.[key];
        return typeof value === 'number'
          ? [
              {
                key,
                label: t(`journal.measurements.${key}`),
                value: t(`journal.measurements.value.${MEASUREMENT_LIMITS[key].unit}`, {
                  value: value.toLocaleString(i18n.language),
                }),
              },
            ]
          : [];
      });

  return (
    <section aria-labelledby="checkin-summary" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 id="checkin-summary" className="kw-kicker">
          {t('journal.checkinHeading')}
        </h3>
        <button
          type="button"
          onClick={onEdit}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('journal.edit')}
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        {[...rows, ...measured]
          .filter((row) => row.value !== null)
          .map((row) => (
            <div key={row.key} className="flex flex-col">
              <dt className="text-xs text-ink-muted">{row.label}</dt>
              <dd className="font-semibold">{row.value}</dd>
            </div>
          ))}
      </dl>
      {entry.note ? <p className="text-sm whitespace-pre-wrap">{entry.note}</p> : null}
      {missing.length > 0 ? (
        <p className="text-xs text-ink-muted">
          {t('journal.notEnteredList', { list: missing.join(', ') })}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Gewicht des Tages in kg — Komma oder Punkt, gespeichert beim Verlassen des
 * Feldes. Leer heißt „nicht gewogen", nie 0. Unsinn (825 statt 82,5) wird
 * gesagt, nicht gespeichert.
 */
export function WeightField({
  date,
  value,
}: {
  date: string;
  value: number | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [text, setText] = useState(value === null ? '' : formatKg(value, i18n.language));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setText(value === null ? '' : formatKg(value, i18n.language));
  }, [value, i18n.language]);

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="kw-label">
        {t('journal.weight')}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          inputMode="decimal"
          value={text}
          placeholder={t('journal.weightHint')}
          aria-invalid={invalid}
          onChange={(event) => {
            setText(event.target.value);
            setInvalid(false);
          }}
          onBlur={() => {
            const trimmed = text.trim();
            if (trimmed === '') {
              if (value !== null) void saveBodyWeight(date, null, db);
              return;
            }
            const kg = Number(trimmed.replace(',', '.'));
            if (!Number.isFinite(kg) || kg < 20 || kg > 400) {
              setInvalid(true);
              return;
            }
            const rounded = Math.round(kg * 10) / 10;
            if (rounded !== value) void saveBodyWeight(date, rounded, db);
          }}
          className="kw-field w-28"
        />
        <span aria-hidden="true" className="text-sm text-ink-muted">
          kg
        </span>
      </div>
      {invalid ? (
        <span role="alert" className="text-sm text-danger">
          {t('journal.weightInvalid')}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Körpermaße eingeklappt unter dem Gewicht — man nimmt sie alle paar Wochen,
 * nicht jeden Morgen. Gespeichert wird je Feld beim Verlassen; leer heißt
 * „nicht gemessen“, Unsinn wird gesagt, nicht gespeichert.
 */
function MeasurementFields({
  date,
  entry,
}: {
  date: string;
  entry: LocalJournalEntry | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const measured = entry?.measurements ?? null;
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const count = measured ? Object.keys(measured).length : 0;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
      >
        {count > 0
          ? t('journal.measurements.toggleCount', { count })
          : t('journal.measurements.toggle')}
      </button>
      {open ? (
        <div id={panelId} className="flex flex-col gap-2">
          <p className="text-xs text-ink-muted">{t('journal.measurements.how')}</p>
          <div className="grid grid-cols-2 gap-2">
            {MEASUREMENT_KEYS.map((key) => (
              <MeasurementField
                key={key}
                date={date}
                measure={key}
                value={measured?.[key] ?? null}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MeasurementField({
  date,
  measure,
  value,
}: {
  date: string;
  measure: MeasurementKey;
  value: number | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const id = useId();
  const { min, max, unit } = MEASUREMENT_LIMITS[measure];
  const format = (number: number) =>
    number.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const [text, setText] = useState(value === null ? '' : format(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setText(value === null ? '' : format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, i18n.language]);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-xs text-ink-muted">
        {t(`journal.measurements.${measure}`)} ({unit})
      </label>
      <input
        id={id}
        inputMode="decimal"
        value={text}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${id}-error` : undefined}
        onChange={(event) => {
          setText(event.target.value);
          setInvalid(false);
        }}
        onBlur={() => {
          const trimmed = text.trim();
          if (trimmed === '') {
            if (value !== null) void saveMeasurement(date, measure, null, db);
            return;
          }
          const number = Math.round(Number(trimmed.replace(',', '.')) * 10) / 10;
          if (!measurementInRange(measure, number)) {
            setInvalid(true);
            return;
          }
          if (number !== value) void saveMeasurement(date, measure, number, db);
        }}
        className="kw-field"
      />
      {invalid ? (
        <span id={`${id}-error`} role="alert" className="text-xs text-danger">
          {t('journal.measurements.invalid', { min, max, unit })}
        </span>
      ) : null}
    </div>
  );
}

function formatKg(kg: number, language: string): string {
  return kg.toLocaleString(language, { maximumFractionDigits: 1 });
}
